import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function fmtTamano(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FiltroSecciones({ categorias, seccion, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        size="sm"
        variant={seccion === "todas" ? "default" : "outline"}
        onClick={() => onChange("todas")}
      >
        Todas
      </Button>
      {categorias.map((c) => (
        <Button
          key={c.id}
          type="button"
          size="sm"
          variant={seccion === String(c.id) ? "default" : "outline"}
          onClick={() => onChange(String(c.id))}
        >
          {c.nombre}
        </Button>
      ))}
    </div>
  );
}

function ListaMisDocumentos({ items, onDescargar }) {
  if (items.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-indigo-100 py-12 text-center text-sm text-zinc-500">
        No tienes documentos en esta sección.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((doc) => (
        <div key={doc.id} className="rounded-xl border border-indigo-100 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-zinc-900">{doc.concepto}</p>
              <p className="text-xs text-zinc-500">{doc.nombre_archivo} · {fmtTamano(doc.tamano_bytes)}</p>
              <p className="mt-1 text-xs text-zinc-400">Publicado {fmtFecha(doc.created_at)} {fmtHora(doc.created_at)}</p>
              {doc.descargado_por_mi ? (
                <Badge variant="outline" className="mt-2 border-emerald-200 text-emerald-700">Ya descargado</Badge>
              ) : (
                <Badge className="mt-2 bg-amber-100 text-amber-800">Pendiente de consultar</Badge>
              )}
            </div>
            <Button size="sm" className="shrink-0 gap-1.5" onClick={() => onDescargar(doc)}>
              <Download className="h-4 w-4" />
              Descargar
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MisDocumentos() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [seccion, setSeccion] = useState("todas");
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const params = seccion !== "todas" ? { categoria_id: seccion } : {};
      const { data } = await api.get("/documentos", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [seccion]);

  useEffect(() => {
    setCargando(true);
    cargar();
  }, [cargar]);

  useEffect(() => {
    api.get("/documentos/categorias")
      .then(({ data }) => setCategorias(data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const descargar = async (doc) => {
    try {
      const { data } = await api.get(`/documentos/${doc.id}/archivo`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.nombre_archivo || "documento.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="Mis documentos"
        subtitle="Consulta y descarga la documentación que te corresponde"
        gradient="from-slate-600 via-indigo-600 to-violet-600"
      />

      <FiltroSecciones categorias={categorias} seccion={seccion} onChange={setSeccion} />

      <div>
        {cargando ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : (
          <ListaMisDocumentos items={items} onDescargar={descargar} />
        )}
      </div>
    </div>
  );
}
